import { FormOptions } from 'constants/form';
import { showToast } from 'helpers/showToast';
import {
  AutoImportPostTiming,
  AutoImportPostTimingOldPosts,
  AutoImportStatus,
  CategoryFieldsFragment,
  CreateAutoImportInput,
  CreateAutoImportMutationOptions,
  EditAutoImportMutationOptions,
  GetSocialProfilesQuery,
  PlanUsageType,
  UpdateAutoImportInput,
  UsageFieldsFragmentDoc
} from 'models/graphql';
import { AppContext } from 'providers/AppProvider';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useForm, UseFormProps, UseFormReturn, useWatch } from 'react-hook-form';
import { AutoImportQueryResult, CategoriesResult } from '../AutoImporters.view';
import { CreateEditImporterModalView } from './CreateEditImporterModal.view';
import { SelectedSocialProfileValue } from '../../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { mapProfileFragmentToSelectedSocialProfileValue } from '../../../PostEditor/helpers/mapProfileFragmentToSelectedSocialProfileValue';
import { setProfileSelectorFormValues } from '../../../../components/socialProfiles/SocialProfileSelector/SocialProfileSelector.view';
import { isArray } from 'lodash';
import { useApolloClient } from '@apollo/client';

export type newRssImportType = {
  url: string;
  term: string;
};

export type CreateEditImporterModalControllerProps = {
  onClose: VoidFunction;
  loading: boolean;
  categories: CategoriesResult[];
  socialProfiles: GetSocialProfilesQuery['workspace']['socialProfiles'][number][];
  createAutoImport: (options: CreateAutoImportMutationOptions, callback: (success: boolean) => void) => void;
  editAutoImport: (options: EditAutoImportMutationOptions, callback: (success: boolean) => void) => void;
  newRssAutoImport?: newRssImportType;
  autoImporter?: AutoImportQueryResult;
};

export type CreateEditImportFormValues = {
  source: string;
  name: string;
  shouldRefineSearch: string;
  refineSearchTerm: string;
  shouldModifyArticle: string;
  refineSearchTermPrefix: string;
  refineSearchTermSuffix: string;
  category: string;
  profiles: Array<string | false>;
  profiles_checkboxes: Array<string | false>;
  profiles_pinterestBoard: Array<string | false>;
  profiles_pinterestBoard_array: Array<string | false>;
  howSave: AutoImportPostTiming | undefined;
  recycling: string;
  expireAfterCheck: boolean;
  qtyTimesPost?: number;
  shouldImportOldPosts: string;
  whereImport?: AutoImportPostTimingOldPosts;
  pauseImport: string;
  select_all_profiles: 'true' | 'false';
};

export const CreateEditImporterModalController = ({
  onClose,
  autoImporter,
  newRssAutoImport,
  categories,
  socialProfiles,
  createAutoImport,
  editAutoImport,
  loading: queryLoading
}: CreateEditImporterModalControllerProps) => {
  const { selectedWorkspaceId } = useContext(AppContext);
  const [loading, setLoading] = useState<boolean>(queryLoading);

  useEffect(() => {
    setLoading(queryLoading);
  }, [queryLoading]);

  const preselectedProfiles: SelectedSocialProfileValue[] = useMemo(() => {
    if (!autoImporter) return [];
    const profiles =
      autoImporter.socialProfiles.map(profile => ({
        __typename: profile.__typename,
        profileId: profile.id
      })) ?? [];
    return profiles.map((profile, _index) => {
      if (profile.__typename === 'PinterestSocialProfile') {
        const pinProfileBoard = autoImporter.pinterestBoards.find(
          pinterestProfile => pinterestProfile.socialProfileId === profile.profileId
        );
        return {
          ...profile,
          ...(pinProfileBoard ? { pnBoardId: pinProfileBoard.pinterestBoardId } : {})
        };
      }
      return profile;
    });
  }, [autoImporter]);

  const profilesFormValue = useMemo(
    () =>
      socialProfiles.map(p => {
        const preselectedProfilesIds = preselectedProfiles.map(psp => psp.profileId);
        return preselectedProfilesIds.includes(p.id) ? p.id : false;
      }),
    [preselectedProfiles, socialProfiles]
  );

  const pnBoardsFormValues = useMemo(
    () =>
      socialProfiles.map(p => {
        const preselectdValue = preselectedProfiles.find(psp => psp.profileId === p.id);
        return preselectdValue?.pnBoardId ? preselectdValue.pnBoardId : false;
      }),
    [preselectedProfiles, socialProfiles]
  );

  const defaultValues: Partial<CreateEditImportFormValues> = {
    source: autoImporter?.url || newRssAutoImport?.url || '',
    name: autoImporter?.name || '',
    shouldRefineSearch: autoImporter?.refineByTerm || newRssAutoImport?.term ? 'true' : 'false',
    refineSearchTerm: autoImporter?.refineByTerm || newRssAutoImport?.term || '',
    shouldModifyArticle: autoImporter?.prefixText || autoImporter?.suffixText ? 'true' : 'false',
    refineSearchTermPrefix: autoImporter?.prefixText || '',
    refineSearchTermSuffix: autoImporter?.suffixText || '',
    category: autoImporter?.category.id || '',
    howSave: autoImporter?.postTiming,
    recycling: autoImporter?.recycle ? 'true' : 'false',
    profiles: profilesFormValue,
    profiles_checkboxes: profilesFormValue,
    profiles_pinterestBoard: pnBoardsFormValues,
    profiles_pinterestBoard_array: pnBoardsFormValues,
    expireAfterCheck: !!autoImporter?.recycleMaxRepeats,
    qtyTimesPost: autoImporter?.recycleMaxRepeats,
    shouldImportOldPosts: 'false',
    whereImport: undefined,
    pauseImport: ''
  };
  const formOptions = { ...FormOptions } as UseFormProps<CreateEditImportFormValues>;
  const useFormInstance = useForm<CreateEditImportFormValues>({ ...formOptions, defaultValues });
  const { handleSubmit, control, setValue } = useFormInstance;
  const { cache } = useApolloClient();

  const [selectedProfiles, setSelectedProfiles] = useState<SelectedSocialProfileValue[]>(preselectedProfiles);
  const selectedProfilesValuesArray: Array<string | boolean> | undefined = useWatch({
    control: control,
    name: 'profiles'
  });

  const valueCategory: string = useWatch({
    control: control,
    name: 'category',
    defaultValue: autoImporter?.category.id || ''
  });

  useEffect(() => {
    let profilesFormValue: (string | boolean)[] | string = useFormInstance.getValues('profiles');
    if (!isArray(profilesFormValue)) {
      // if we clicked select all, the values to the profiles might not be propagated, and if that's the case, we need to rely on the checkboxes values
      profilesFormValue = useFormInstance.getValues('profiles_checkboxes');
    }
    if (!isArray(profilesFormValue)) return;
    const hasSelectedProfiles = profilesFormValue.filter(p => !!p).length > 0;
    const defaultCategoryProfiles: CategoryFieldsFragment['defaultSocialProfiles'] =
      categories.find(c => c.id === valueCategory)?.defaultSocialProfiles ?? [];

    if (hasSelectedProfiles || !valueCategory || defaultCategoryProfiles.length === 0) {
      return;
    }

    const mappedDefaultProfiles = mapProfileFragmentToSelectedSocialProfileValue(defaultCategoryProfiles);
    setSelectedProfiles(mappedDefaultProfiles);
    const castedFormInstance = useFormInstance as unknown as UseFormReturn;
    setProfileSelectorFormValues(socialProfiles, mappedDefaultProfiles, castedFormInstance, 'profiles');
  }, [valueCategory, categories, useFormInstance, socialProfiles]);

  useEffect(() => {
    setSelectedProfiles(preselectedProfiles);
  }, [preselectedProfiles]);

  useEffect(() => {
    if (isArray(selectedProfilesValuesArray)) {
      const pnBoards = useFormInstance.getValues('profiles_pinterestBoard');
      const mappedProfiles: SelectedSocialProfileValue[] = socialProfiles.flatMap((profile, index) => {
        if (selectedProfilesValuesArray.includes(profile.id)) {
          if (profile.__typename === 'PinterestSocialProfile') {
            let pnBoardId: string | undefined;
            if (isArray(pnBoards) && pnBoards[index]) {
              pnBoardId = pnBoards[index] as string;
            }
            return {
              __typename: profile.__typename,
              profileId: profile.id,
              pnBoardId
            };
          }
          return {
            __typename: profile.__typename,
            profileId: profile.id
          };
        }
        return [];
      });
      setSelectedProfiles(old => (JSON.stringify(old) !== JSON.stringify(mappedProfiles) ? mappedProfiles : old));
    }
  }, [selectedProfilesValuesArray, socialProfiles, useFormInstance]);

  useEffect(() => {
    if (!autoImporter) {
      useFormInstance.reset();
    }
    return () => useFormInstance.reset();
  }, [autoImporter, useFormInstance]);

  const onSubmit = handleSubmit(async data => {
    const result = await useFormInstance.trigger();
    if (!result) return;
    setLoading(true);
    const {
      name,
      category,
      profiles,
      whereImport,
      shouldModifyArticle,
      refineSearchTermPrefix,
      refineSearchTermSuffix,
      qtyTimesPost,
      refineSearchTerm,
      source,
      recycling,
      pauseImport,
      howSave,
      expireAfterCheck,
      shouldImportOldPosts,
      profiles_pinterestBoard,
      shouldRefineSearch
    } = data;

    const trimmedName = name.trim().replace(/\s+/g, ' ');
    setValue('name', trimmedName);
    if (trimmedName === '') {
      useFormInstance.trigger('name');
      return;
    }

    const pinterestBoards =
      profiles_pinterestBoard && profiles_pinterestBoard.length
        ? profiles_pinterestBoard
            .flatMap((boardId, index) =>
              boardId && profiles && profiles[index]
                ? {
                    socialProfileId: profiles[index] as string,
                    pinterestBoardId: boardId
                  }
                : []
            )
            .filter(Boolean)
        : [];

    const socialProfileIds = profiles.flatMap(value => (typeof value === 'string' && value ? value : []));

    if (!autoImporter) {
      if (!howSave) {
        useFormInstance.trigger('howSave');
        return;
      }
      const input: CreateAutoImportInput = {
        name: trimmedName,
        categoryId: category,
        prefixText: shouldModifyArticle === 'true' && refineSearchTermPrefix ? refineSearchTermPrefix : undefined,
        suffixText: shouldModifyArticle === 'true' && refineSearchTermSuffix ? refineSearchTermSuffix : undefined,
        recycle: recycling === 'true',
        recycleMaxRepeats: expireAfterCheck ? qtyTimesPost : undefined,
        refineByTerm: shouldRefineSearch === 'true' ? refineSearchTerm : undefined,
        status: pauseImport ? AutoImportStatus.PAUSED : AutoImportStatus.ACTIVE,
        socialProfileIds,
        pinterestBoards,
        url: source,
        postTiming: howSave
      };

      if (shouldImportOldPosts === 'true' && whereImport) {
        input.importOldPosts = { timing: whereImport };
      }

      const callback = (success: boolean) => {
        setLoading(false);
        if (success) {
          showToast({
            severity: 'success',
            detail: 'Auto Importer has been created.',
            summary: 'Create Auto Importer'
          });
          onClose();
          const oldUsage: PlanUsageType | null = cache.readFragment({
            fragment: UsageFieldsFragmentDoc,
            fragmentName: 'UsageFields',
            id: 'PlanUsageType:{}'
          });
          const newUsage = { ...oldUsage, autoImports: (oldUsage?.autoImports ?? 0) + 1 };
          cache.writeFragment({
            fragment: UsageFieldsFragmentDoc,
            fragmentName: 'UsageFields',
            id: 'PlanUsageType:{}',
            data: newUsage
          });
        }
      };
      createAutoImport({ variables: { workspaceId: selectedWorkspaceId, input } }, callback);
    } else {
      const input: UpdateAutoImportInput = {
        name,
        categoryId: category,
        prefixText: shouldModifyArticle === 'true' && refineSearchTermPrefix ? refineSearchTermPrefix : null,
        suffixText: shouldModifyArticle === 'true' && refineSearchTermSuffix ? refineSearchTermSuffix : null,
        recycle: recycling === 'true',
        recycleMaxRepeats: expireAfterCheck ? qtyTimesPost : null,
        refineByTerm: shouldRefineSearch === 'true' ? refineSearchTerm : null,
        status: pauseImport ? AutoImportStatus.PAUSED : AutoImportStatus.ACTIVE,
        socialProfileIds,
        pinterestBoards,
        postTiming: howSave
      }; // im using the state on some variables because the toggle value is a string and not a boolean

      const callback = (success: boolean) => {
        setLoading(false);
        if (success) {
          showToast({
            severity: 'success',
            detail: 'Auto Importer has been updated.',
            summary: 'Update Auto Importer'
          });
          onClose();
        }
      };
      editAutoImport({ variables: { autoImportId: autoImporter.id, input } }, callback);
    }
  });
  const props = {
    useFormInstance,
    loading,
    onClose,
    onSubmit,
    autoImporter,
    newRssAutoImport,
    socialProfiles,
    categories,
    selectedProfiles
  };
  return <CreateEditImporterModalView {...props}></CreateEditImporterModalView>;
};
